// @spec SCF-005
// TanStack Query client factory with optimized defaults

import { QueryClient } from '@tanstack/react-query';

import { ApiClientError } from '@/types/api';

/**
 * Default stale time for queries (60 seconds).
 * Data is considered fresh for this duration and won't refetch.
 */
export const DEFAULT_STALE_TIME = 60 * 1000;

/**
 * Default garbage collection time (5 minutes).
 * Unused query results are kept in cache for this duration.
 */
export const DEFAULT_GC_TIME = 5 * 60 * 1000;

/**
 * Maximum retry delay in milliseconds (30 seconds).
 */
export const MAX_RETRY_DELAY = 30 * 1000;

/**
 * Creates a new QueryClient instance with optimized defaults.
 *
 * Configuration:
 * - staleTime: 60 seconds - balances freshness with network efficiency
 * - gcTime: 5 minutes - keeps unused results available for quick re-access
 * - retry: 3 attempts for network errors, no retry for 4xx client errors
 * - retryDelay: Exponential backoff capped at 30 seconds
 *
 * @returns Configured QueryClient instance
 *
 * @example
 * const queryClient = createQueryClient();
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        gcTime: DEFAULT_GC_TIME,
        retry: (failureCount, error) => {
          // Don't retry on 4xx client errors
          if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => {
          // Exponential backoff: 1s, 2s, 4s, 8s... capped at 30s
          return Math.min(1000 * Math.pow(2, attemptIndex), MAX_RETRY_DELAY);
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false, // Don't retry mutations by default
      },
    },
  });
}
